import { Controller } from "@hotwired/stimulus";
import Swiper from "swiper";
import { Navigation } from "swiper/modules";

/**
 * Trips Slider Controller
 *
 * Handles the Swiper slider for the trips block with navigation arrows
 * and bleed effect (cards extending past container on right side).
 */
export default class extends Controller {
  static targets = ["container", "prevButton", "nextButton"];

  connect() {
    this.initSwiper();
  }

  disconnect() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Initialize Swiper instance
   */
  initSwiper() {
    if (!this.hasContainerTarget) return;

    // Use native CSS scroll-snap on touch devices to avoid iOS Safari
    // memory crashes. translate3d forces all slides into GPU memory;
    // native scrolling lets the browser evict offscreen content.
    this.isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    this.swiper = new Swiper(this.containerTarget, {
      modules: [Navigation],
      cssMode: this.isTouchDevice,
      slidesPerView: "auto",
      spaceBetween: 40, // 40px gap (matches gap-10)
      grabCursor: !this.isTouchDevice,
      watchOverflow: true,
      navigation: {
        prevEl: this.hasPrevButtonTarget ? this.prevButtonTarget : null,
        nextEl: this.hasNextButtonTarget ? this.nextButtonTarget : null,
        disabledClass: "opacity-30 cursor-not-allowed",
      },
      breakpoints: {
        // Mobile: smaller gap
        0: {
          spaceBetween: 16,
        },
        // Tablet and up: 40px gap
        768: {
          spaceBetween: 40,
        },
      },
    });

    this.updateNavigationVisibility();
  }

  /**
   * Navigate to previous slide
   */
  prev() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  /**
   * Navigate to next slide
   */
  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  /**
   * Reinitialize Swiper (called after full content replacement)
   */
  reinitialize() {
    if (this.swiper) {
      this.swiper.destroy(true, false);
      this.swiper = null;
    }
    this.initSwiper();
  }

  /**
   * Lightweight slide update (called after appending slides).
   * Avoids full destroy+recreate which causes iOS Safari crashes
   * due to massive layout reflows with many slides.
   */
  updateSlides() {
    if (this.swiper) {
      this.swiper.update();
      this.updateNavigationVisibility();
    } else {
      this.initSwiper();
    }
  }

  /**
   * Update navigation button visibility based on slides
   */
  updateNavigationVisibility() {
    if (!this.swiper) return;

    const hasEnoughSlides = this.swiper.slides.length > 1;

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.classList.toggle("hidden", !hasEnoughSlides);
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.classList.toggle("hidden", !hasEnoughSlides);
    }
  }

}
